import { fabric } from 'fabric';
import { ThaiTextbox } from '../thai/ThaiTextbox';
import { cleanFx, fxLook, inkOf, plainLook, type FxLook, type TextFx } from './textFx';

/*
 * เอฟเฟกต์ข้อความ on the canvas (src/editor/textFx.ts has the numbers). A text with `textFx` gets its Fabric values
 * (fill, stroke, shadow…) from it every time it is drawn at a new font size or effect; พื้นหลัง (a box with round
 * corners) is drawn behind the letters here and counts in the text's size on the page, so its frame, its handles,
 * snapping and exports all include the box.
 */

type Fx = fabric.Textbox & { textFx?: TextFx | null; __fxKey?: string; __fxBox?: FxLook['box'] };

const boxPad = (o: Fx) => o.__fxBox?.pad ?? 0;

function setLook(o: Fx, look: FxLook) {
  (o as fabric.Object).set({ fill: look.fill, strokeWidth: look.strokeWidth, paintFirst: look.paintFirst } as Partial<fabric.Object>);
  (o as { stroke: string | null }).stroke = look.stroke;
  o.shadow = look.shadow ? new fabric.Shadow({ ...look.shadow, affectStroke: true }) : null as unknown as fabric.Shadow;
  const before = boxPad(o);
  o.__fxBox = look.box;
  o.dirty = true;
  if (before !== boxPad(o) || o.aCoords) o.setCoords();   // the box (and an outline) change the frame
}

/** bring a text's Fabric values in line with its effect (only when the effect or the font size changed) */
export function syncFx(o: fabric.Object) {
  const t = o as Fx;
  const fx = cleanFx(t.textFx);
  if (!fx) { if (t.__fxBox) { t.__fxBox = null; t.__fxKey = undefined; t.setCoords(); } return; }
  const key = JSON.stringify(fx) + '|' + (t.fontSize ?? 0);
  if (t.__fxKey === key) return;
  t.__fxKey = key;
  setLook(t, fxLook(fx, t.fontSize ?? 40));
}

/** put an effect on a text, or take it off (null: the plain text in its own colour again) */
export function setTextFx(o: fabric.Object, fx: TextFx | null) {
  const t = o as Fx;
  const ink = inkOf(cleanFx(t.textFx), t.fill);
  t.textFx = fx;
  t.__fxKey = undefined;
  if (fx) syncFx(t);
  else setLook(t, plainLook(ink));
}

/** the colour the person gave the text (not the one an effect draws it in) */
export const textInk = (o: fabric.Object) => inkOf(cleanFx((o as Fx).textFx), o.fill);

/** change the text's own colour; an effect drawn from it follows */
export function setTextInk(o: fabric.Object, ink: string) {
  const t = o as Fx, fx = cleanFx(t.textFx);
  if (!fx) { t.set('fill', ink); return; }
  setTextFx(t, { ...fx, ink: ink.toLowerCase() });
}

const proto = (ThaiTextbox as unknown as { prototype: Fx }).prototype;
const baseRender = proto.render, baseBackground = (proto as unknown as { _renderBackground(ctx: CanvasRenderingContext2D): void })._renderBackground;
const baseDims = (proto as unknown as { _getNonTransformedDimensions(): { x: number; y: number } })._getNonTransformedDimensions;

Object.assign(proto, {
  render(this: Fx, ctx: CanvasRenderingContext2D) {
    syncFx(this);
    return baseRender.call(this, ctx);
  },
  /** พื้นหลัง: the box behind the letters (inside the object's own drawing, so it moves, turns and fades with it) */
  _renderBackground(this: Fx, ctx: CanvasRenderingContext2D) {
    baseBackground.call(this, ctx);
    const b = this.__fxBox;
    if (!b) return;
    const w = (this.width ?? 0) + 2 * b.pad, h = (this.height ?? 0) + 2 * b.pad;
    const r = Math.min(b.radius, w / 2, h / 2);
    ctx.save();
    ctx.fillStyle = b.color;
    ctx.beginPath();
    ctx.moveTo(-w / 2 + r, -h / 2);
    ctx.arcTo(w / 2, -h / 2, w / 2, h / 2, r);
    ctx.arcTo(w / 2, h / 2, -w / 2, h / 2, r);
    ctx.arcTo(-w / 2, h / 2, -w / 2, -h / 2, r);
    ctx.arcTo(-w / 2, -h / 2, w / 2, -h / 2, r);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  },
  /** the box counts in the text's size: frame, handles, snapping, the area exports and "ดาวน์โหลดที่เลือก" cut */
  _getNonTransformedDimensions(this: Fx) {
    const d = baseDims.call(this), pad = boxPad(this);
    return pad ? { x: d.x + 2 * pad, y: d.y + 2 * pad } : d;
  }
});

/*
 * The side handles change the text's width. Fabric measures from the far side of the frame, which now includes the box:
 * its width would grow by the box twice on the first pull. The same handler as Fabric 5.3's changeWidth, less the box.
 */
const cu = (fabric as unknown as { controlsUtils: {
  wrapWithFireEvent(n: string, h: fabric.Control['actionHandler']): fabric.Control['actionHandler'];
  wrapWithFixedAnchor(h: fabric.Control['actionHandler']): fabric.Control['actionHandler'];
  getLocalPoint(t: unknown, ox: unknown, oy: unknown, x: number, y: number): fabric.Point;
} }).controlsUtils;
type Transform = { target: Fx; originX: string; originY: string };
export function widthFromPointer(localX: number, centered: boolean, scaleX: number, strokePadding: number, pad: number): number {
  return Math.max(0, Math.abs(localX * (centered ? 2 : 1) / scaleX) - strokePadding - 2 * pad);
}
const changeWidth = ((_e: MouseEvent, transform: Transform, x: number, y: number) => {
  const t = transform.target, lp = cu.getLocalPoint(transform, transform.originX, transform.originY, x, y);
  const strokePadding = (t.strokeWidth ?? 0) / (t.strokeUniform ? t.scaleX ?? 1 : 1);
  const old = t.width, w = widthFromPointer(lp.x, transform.originX === 'center' && transform.originY === 'center', t.scaleX ?? 1, strokePadding, boxPad(t));
  t.set('width', w);
  return old !== w;
}) as unknown as fabric.Control['actionHandler'];
const resize = cu.wrapWithFireEvent('resizing', cu.wrapWithFixedAnchor(changeWidth));
for (const k of ['ml', 'mr']) {
  const c = (fabric.Textbox.prototype.controls as Record<string, fabric.Control>)[k];
  if (c) c.actionHandler = resize;
}
